import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldOff, Loader2, Copy, Check, KeyRound, AlertTriangle } from "lucide-react";
import api from "../lib/api";

export default function SecuritySettingsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  // Enrollment flow: idle → setup (QR + code) → recovery (codes shown once)
  const [enrollStep, setEnrollStep] = useState("idle");
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_url}
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable form
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableUseRecovery, setDisableUseRecovery] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/auth/2fa/status")
      .then((r) => { if (alive) setEnabled(!!r.data.enabled); })
      .catch(() => { /* leave defaults */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupData(r.data);
      setVerifyCode("");
      setEnrollStep("setup");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start 2FA setup");
    } finally { setBusy(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = verifyCode.trim();
    if (!code) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", { code });
      setRecoveryCodes(r.data.recovery_codes || []);
      setEnrollStep("recovery");
      toast.success("Two-factor authentication enabled");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Invalid verification code");
    } finally { setBusy(false); }
  };

  const confirmSaved = () => {
    setEnabled(true);
    setEnrollStep("idle");
    setSetupData(null);
    setRecoveryCodes([]);
    setVerifyCode("");
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!disablePassword || !disableCode.trim()) return;
    setBusy(true);
    try {
      const payload = { password: disablePassword };
      if (disableUseRecovery) payload.recovery_code = disableCode.trim();
      else payload.code = disableCode.trim();
      await api.post("/auth/2fa/disable", payload);
      setEnabled(false);
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      toast.success("Two-factor authentication disabled");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to disable 2FA");
    } finally { setBusy(false); }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(setupData?.secret || "");
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const copyAllCodes = () => {
    navigator.clipboard?.writeText(recoveryCodes.join("\n"));
    setCopiedCodes(true);
    toast.success("Recovery codes copied");
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="security-settings-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/settings/privacy")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="security-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <ShieldCheck className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Security</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        <section data-testid="twofa-section">
          <h2 className="font-outfit text-lg font-medium text-foreground mb-1">Two-Factor Authentication</h2>
          <p className="text-xs text-muted-foreground mb-4">Add an extra layer of protection with an authenticator app (TOTP)</p>

          {loading ? (
            <div className="bg-card border border-border/60 rounded-xl p-8 flex justify-center" data-testid="twofa-loading">
              <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl p-5 space-y-4">
              {/* Status row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${enabled ? "bg-emerald-500/10" : "bg-muted/40"}`}>
                    {enabled ? <ShieldCheck className="w-4.5 h-4.5 text-emerald-400" /> : <ShieldOff className="w-4.5 h-4.5 text-muted-foreground/70" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Authenticator app</p>
                    <span className={`inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-muted/70 text-muted-foreground"}`} data-testid="twofa-status-badge">
                      {enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
                {!enabled && enrollStep === "idle" && (
                  <Button onClick={startSetup} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-9 text-xs px-4 rounded-lg press-down" data-testid="enable-2fa-btn">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Enable 2FA
                  </Button>
                )}
                {enabled && !showDisable && (
                  <Button onClick={() => setShowDisable(true)} variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/15 h-9 text-xs px-4 rounded-lg" data-testid="disable-2fa-btn">
                    Disable
                  </Button>
                )}
              </div>

              {/* Enrollment: QR + secret + verify code */}
              {!enabled && enrollStep === "setup" && setupData && (
                <div className="border-t border-border/40 pt-4 space-y-4" data-testid="twofa-setup-panel">
                  <p className="text-xs text-muted-foreground">1. Scan this QR code with your authenticator app (Google Authenticator, 1Password, Authy...).</p>
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="bg-card border border-border rounded-xl p-3" data-testid="twofa-qr">
                      <QRCodeSVG value={setupData.otpauth_url} size={148} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
                      <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2">
                        <code className="text-xs text-foreground font-mono break-all flex-1" data-testid="twofa-secret">{setupData.secret}</code>
                        <button onClick={copySecret} className="text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer flex-shrink-0" data-testid="copy-secret-btn" aria-label="Copy secret">
                          {copiedSecret ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <form onSubmit={handleVerify} className="space-y-3">
                    <p className="text-xs text-muted-foreground">2. Enter the 6-digit code from your app to confirm:</p>
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        className="w-36 px-3 py-2 bg-muted/40 border border-border rounded-lg text-base tracking-[0.3em] text-center font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
                        data-testid="twofa-verify-input"
                      />
                      <Button type="submit" disabled={busy || verifyCode.length !== 6} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-10 text-xs px-4 rounded-lg press-down" data-testid="twofa-verify-btn">
                        {busy ? "Verifying..." : "Verify & Enable"}
                      </Button>
                    </div>
                  </form>
                  <button onClick={() => { setEnrollStep("idle"); setSetupData(null); }} className="text-xs text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer" data-testid="twofa-cancel-setup">Cancel setup</button>
                </div>
              )}

              {/* Recovery codes — shown exactly once */}
              {enrollStep === "recovery" && (
                <div className="border-t border-border/40 pt-4 space-y-3" data-testid="twofa-recovery-panel">
                  <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-300">Save these recovery codes somewhere safe. Each can be used once to sign in if you lose your authenticator. <strong>They will not be shown again.</strong></p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 bg-muted/40 border border-border rounded-lg p-4" data-testid="twofa-recovery-codes">
                    {recoveryCodes.map((c) => (
                      <code key={c} className="text-xs font-mono text-foreground">{c}</code>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={copyAllCodes} variant="outline" className="border-border text-foreground/80 bg-card h-9 text-xs px-4 rounded-lg" data-testid="copy-recovery-codes-btn">
                      {copiedCodes ? <Check className="w-3 h-3 mr-1 text-emerald-500" /> : <Copy className="w-3 h-3 mr-1" />} Copy all
                    </Button>
                    <Button onClick={confirmSaved} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-9 text-xs px-4 rounded-lg press-down" data-testid="twofa-saved-btn">
                      I saved them
                    </Button>
                  </div>
                </div>
              )}

              {/* Disable form */}
              {enabled && showDisable && (
                <form onSubmit={handleDisable} className="border-t border-border/40 pt-4 space-y-3" data-testid="twofa-disable-panel">
                  <p className="text-xs text-muted-foreground">Confirm your password and a {disableUseRecovery ? "recovery code" : "6-digit authenticator code"} to turn off 2FA.</p>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Password</label>
                    <input
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder="Your account password"
                      className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
                      data-testid="disable-password-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{disableUseRecovery ? "Recovery code" : "Authenticator code"}</label>
                    <input
                      value={disableCode}
                      onChange={(e) => setDisableCode(disableUseRecovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder={disableUseRecovery ? "Enter recovery code" : "000000"}
                      inputMode={disableUseRecovery ? "text" : "numeric"}
                      className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
                      data-testid="disable-code-input"
                    />
                    <button type="button" onClick={() => { setDisableUseRecovery(!disableUseRecovery); setDisableCode(""); }} className="text-xs text-cyan-400 hover:text-cyan-300 bg-transparent border-0 cursor-pointer mt-1.5 flex items-center gap-1" data-testid="disable-recovery-toggle">
                      <KeyRound className="w-3 h-3" /> {disableUseRecovery ? "Use an authenticator code instead" : "Use a recovery code instead"}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={busy || !disablePassword || !disableCode.trim()} className="bg-red-500 hover:bg-red-600 text-white h-9 text-xs px-4 rounded-lg disabled:opacity-40" data-testid="confirm-disable-2fa-btn">
                      {busy ? "Disabling..." : "Disable 2FA"}
                    </Button>
                    <button type="button" onClick={() => { setShowDisable(false); setDisablePassword(""); setDisableCode(""); }} className="px-4 text-xs text-muted-foreground bg-muted/70 rounded-lg border-0 cursor-pointer hover:bg-white/10" data-testid="cancel-disable-2fa-btn">Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
